import { MetaradarDailyStateSnapshot } from '@app/model/metaradar/MetaradarDailyStateSnapshot';
import DataProvider from '@app/model/_common/DataProvider';
import MetaradarStateItem from '@app/model/metaradar/MetaradarStateItem';
import Region from '@app/model/market_state/Region';
import Utilities from '@util/Utilities';
import moment from 'moment';

export interface UndercutStatistics {
	provider: DataProvider;
	iconUrl: string;
	undercutCount: number;
	avgDifference: number;
	percentageOfTotalUndercuts: number;
	percentageOfTotalUndercutsPercentage: string;
}

export class MetaradarStateSnapshot {

	companyHotelId: number;
	lastUpdateDate: string;
	dailyItems: MetaradarDailyStateSnapshot[] = [];

	constructor(data: MetaradarStateSnapshot) {
		Object.assign(this, data);
		if (data.dailyItems) this.dailyItems = data.dailyItems.map(item => new MetaradarDailyStateSnapshot(item));
	}

	public countProviderUndercutsDays(providers: DataProvider[], threshold: number): {undercutDays: number, allDays: number} {
		let undercutDays = this.dailyItems.filter(dailyItem=>dailyItem.isUndercutByOneOfTheProviders(providers, threshold)).length
		return {
			undercutDays: undercutDays,
			allDays: this.dailyItems.length
		}
	}

	public calculateUndercutRegionsDaysPercentage(providers: DataProvider[], threshold: number): number {
		if (!this.dailyItems || this.dailyItems.length === 0) {
			return 0;
		}

		let totalUndercutRegionDays = 0;
		let totalRegionDays = 0;

		for (const dailyItem of this.dailyItems) {
			const { undercutRegions, totalRegions } = dailyItem.countUndercutRegions(providers, threshold);
			totalUndercutRegionDays += undercutRegions;
			totalRegionDays += totalRegions;
		}

		if (totalRegionDays === 0) return 0;

		return (totalUndercutRegionDays / totalRegionDays) * 100;
	}

	public findUndercutsByStatDate(threshold: number): {
		brandOffer: MetaradarStateItem;
		statDate: string,
		difference: number,
		percentage: number,
		item: MetaradarStateItem
	}[] {
		const result: {
			brandOffer: MetaradarStateItem;
			statDate: string,
			difference: number,
			percentage: number,
			item: MetaradarStateItem
		}[] = [];

		for (const dailyItem of this.dailyItems) {
			const undercutsResult = dailyItem.findUndercuts(threshold);

			if (!!undercutsResult && undercutsResult.length > 0) {
				undercutsResult.forEach(({ brandOffer, undercuts }) => {
					return undercuts.forEach(undercut => {
						const difference = brandOffer.price.amount - undercut.price.amount;
						const percentage = (difference / brandOffer.price.amount) * 100;
						result.push({
							brandOffer,
							statDate: dailyItem.statDate,
							difference,
							percentage,
							item: undercut,
						});
					})
				});
			}
		}

		return result;
	}

	private countAllProviderItems(provider: DataProvider) {
		return this.dailyItems.map(dailyItem=> dailyItem.countProviderItems(provider)).reduce((acc, val) => acc + val, 0);
	}

	private countAllProviderPerRegionItems(provider: DataProvider, region: Region) {
		return this.dailyItems.map(dailyItem=> dailyItem.countProviderPerRegionItems(provider, region)).reduce((acc, val) => acc + val, 0);
	}

	public findStatisticsPerProvider(threshold: number): UndercutStatistics[] | undefined {
		const providerStats = new Map<DataProvider, { totalDifference: number, count: number, iconUrl: string }>();

		for (const dailyItem of this.dailyItems) {
			const dailyDifferences = dailyItem.findDifferencePerProvider(threshold);
			for (const [provider, { totalDifference, count, iconUrl }] of dailyDifferences.entries()) {
				const existing = providerStats.get(provider) || { totalDifference: 0, count: 0, iconUrl: '' };
				providerStats.set(provider, {
					totalDifference: existing.totalDifference + totalDifference,
					count: existing.count + count,
					iconUrl: iconUrl,
				});
			}
		}


		const result: UndercutStatistics[] = [];
		for (const [provider, { totalDifference, count, iconUrl }] of providerStats.entries()) {

			const all = this.countAllProviderItems(provider);

			result.push({
				provider: provider,
				iconUrl: iconUrl,
				undercutCount: count,
				avgDifference: totalDifference / count,
				percentageOfTotalUndercuts: count/all,
				// @ts-ignore
				percentageOfTotalUndercutsPercentage: `${Utilities.formatDouble((count/all)*100, 0)}% (${count}/${all})`,
			});
		}


		return result;
	}

	public findUndercutsPerRegionForProvider(threshold: number, provider: DataProvider): Map<string, {
		region: Region,
		count: number,
		avgUndercutPercentage: number,
		all: number
	}> {
		const regionStats = new Map<Region, { totalPercentage: number, count: number, all: number }>();

		for (const dailyItem of this.dailyItems) {
			const undercutsByRegion = dailyItem.findUndercuts(threshold);

			if (undercutsByRegion) {
				for (const { brandOffer, undercuts } of undercutsByRegion) {
					const providerUndercuts = undercuts.filter(u => u.provider === provider);
					if (providerUndercuts.length > 0) {

						let totalPercentageForRegion = 0;
						for (const undercut of providerUndercuts) {
							if (undercut.price && brandOffer.price) {
								const difference = brandOffer.price.amount - undercut.price.amount;
								const percentageDifference = (difference / brandOffer.price.amount) * 100;
								totalPercentageForRegion += percentageDifference;
							}
						}

						const currentStats = regionStats.get(brandOffer.region) || { totalPercentage: 0, count: 0 };
						regionStats.set(brandOffer.region, {
							totalPercentage: currentStats.totalPercentage + totalPercentageForRegion,
							count: currentStats.count + providerUndercuts.length,
							all: this.countAllProviderPerRegionItems(provider, brandOffer.region)
						});
					}
				}
			}
		}

		const result = new Map<string, { region: Region, count: number, avgUndercutPercentage: number, all: number }>();
		for (const [region, stats] of regionStats.entries()) {
			const avgUndercutPercentage = stats.count > 0 ? stats.totalPercentage / stats.count : 0;
			result.set(region, { region, count: stats.count, avgUndercutPercentage, all: stats.all });
		}
		return result;
	}

	public findParsingPeriodInMonths() {
		if (!this.dailyItems || this.dailyItems.length < 2) {
			return 0;
		}

		const dates = this.dailyItems.map(item => moment(item.statDate));
		const minDate = moment.min(dates);
		const maxDate = moment.max(dates);

		return maxDate.diff(minDate, 'months');
	}
}